import {ProcessSchemaMapperType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ProcessFlowchartSchemaMapperConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';

import {ProcessFlowchartConfigVersionGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigVersionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

type AccessibleFromSchema = ExtractByTypename<
  ProcessFlowchartConfigVersionGraphQL['schemaMapperConfigs'][number]['schemaFieldMapperConfigs'][number]['fromSchema'],
  'ProcessOutputSchemaNode'
>;

type AccessibleToSchema = ExtractByTypename<
  ProcessFlowchartConfigVersionGraphQL['schemaMapperConfigs'][number]['toSchema'],
  'ProcessTaskSchemaNode'
>;

type AccessibleAvailableTaskSchema = ExtractByTypename<
  ProcessFlowchartConfigVersionGraphQL['availableSchemas'][number],
  'ProcessTaskSchemaNode'
>;

type LookupConfigGql =
  ProcessFlowchartConfigVersionGraphQL['schemaMapperConfigs'][number]['schemaFieldMapperConfigs'][number]['lookupConfig'];
type FlowchartGql = ProcessFlowchartConfigVersionGraphQL['templateFlowchart'];

// The PlaceholderTaskSchema represents a new row in the AvailableTaskSchemasPane which does not yet have a
// defined task schema. These rows still need to have unique IDs so that they can be reordered.
export interface PlaceholderTaskSchema {
  placeholderClientId: string;
}

export type AvailableTaskSchema = ProcessTaskSchemaWithoutFlowchartConfigGraphQL | PlaceholderTaskSchema;

export function isPlaceholderTaskSchema(schema: AvailableTaskSchema): schema is PlaceholderTaskSchema {
  return (schema as PlaceholderTaskSchema).placeholderClientId !== undefined;
}

export interface ClientSchemaMapperConfig {
  clientId: string;
  fromTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  toTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null;
  mapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null;
}

export interface ClientFlowchartConfig {
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  availableTaskSchemas: Array<AvailableTaskSchema>;
  templateFlowchart: ProcessInteractiveFlowchart | null;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
}

export type DefinedClientSchemaMapperConfig = ClientSchemaMapperConfig & {
  mapperConfig: NonNullable<ClientSchemaMapperConfig['mapperConfig']>;
  toTaskSchema: NonNullable<ClientSchemaMapperConfig['toTaskSchema']>;
};

export function isClientSchemaMapperConfigFullyDefined(
  clientSchemaMapperConfig: ClientSchemaMapperConfig
): clientSchemaMapperConfig is DefinedClientSchemaMapperConfig {
  return (
    clientSchemaMapperConfig.fromTaskSchemas.length > 0 &&
    clientSchemaMapperConfig.toTaskSchema != null &&
    clientSchemaMapperConfig.mapperConfig != null
  );
}

export interface AccessibleFlowchartConfigVersion {
  __typename: 'ProcessFlowchartConfigVersionNode';
  id: string;
  availableSchemas: Array<AccessibleAvailableTaskSchema>;
  templateFlowchart: FlowchartGql | null;
  schemaMapperConfigs: Array<{
    __typename: 'ProcessFlowchartSchemaMapperConfigNode';
    id: string;
    type: ProcessSchemaMapperType;
    toSchema: AccessibleToSchema;
    schemaFieldMapperConfigs: Array<{
      __typename: 'ProcessFlowchartSchemaFieldMapperConfigNode';
      id: string;
      lookupConfig: LookupConfigGql;
      fromSchema: AccessibleFromSchema;
      toSchemaField: JSONScalar;
      resultMustBeNonNullToMap: boolean;
    }>;
  }>;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
}
